"use strict";
/*
 * IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: IBM Cognos Analytics
 *
 * (C) Copyright IBM Corp. 2020, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 */
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importStar(require("react"));
const carbon_components_react_1 = require("carbon-components-react");
const StringResource_1 = require("../nls/StringResource");
const utils_1 = require("../utils");
const COMPONENT_NAME = 'CodeSnippet';
const DISPLAY_NAME = (0, utils_1.getDisplayName)(COMPONENT_NAME);
const DEFAULT_TID = (0, utils_1.getDefaultTid)(COMPONENT_NAME);
const ARIALABEL = StringResource_1.stringResource.get('codeSnippetCopyText');
const CLASSNAME_WRAP = (0, utils_1.getClassName)(COMPONENT_NAME, 'wrap');
const COPYBUTTONDESCRIPTION = StringResource_1.stringResource.get('codeSnippetCopy');
const COPYLABEL = StringResource_1.stringResource.get('codeSnippetCopyText');
const FEEDBACK = StringResource_1.stringResource.get('codeSnippetCopied');
const SHOWLESSTEXT = StringResource_1.stringResource.get('codeSnippetShowLess');
const SHOWMORETEXT = StringResource_1.stringResource.get('codeSnippetShowMore');
class ForwardRefCodeSnippet extends react_1.Component {
    constructor() {
        var _a;
        super(...arguments);
        this.localCopyToClipboard = () => {
            const textArea = this._input;
            const isiOSDevice = navigator.userAgent.match(/ipad|iphone/i);
            if (isiOSDevice) {
                const editable = textArea.contentEditable;
                const { readOnly } = textArea;
                textArea.contentEditable = 'true';
                textArea.readOnly = false;
                const range = document.createRange();
                range.selectNodeContents(textArea);
                const selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
                textArea.setSelectionRange(0, 999999);
                textArea.contentEditable = editable;
                textArea.readOnly = readOnly;
            }
            else {
                textArea.select();
            }
            document.execCommand && document.execCommand('Copy');
        };
        // Carbon issue https://github.com/carbon-design-system/carbon/issues/4448#issuecomment-729123543
        // Carbon is to supply default copyToClipboard functionality so client do not have to.
        // Carbon is to supply an optional prop to trigger copyToClipboard  functionality.
        this.invokeCopyToClipboard = (_a = this.props.copyToClipboard) !== null && _a !== void 0 ? _a : !this.props.onClick;
        this.localOnClick = event => {
            var _a, _b;
            this.invokeCopyToClipboard && this.localCopyToClipboard();
            (_b = (_a = this.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        };
    }
    render() {
        const _a = this.props, { 
        // remove - remapped and local props
        ariaLabel, className, copyButtonDescription, copyLabel, copyToClipboard, // eslint-disable-line @typescript-eslint/no-unused-vars
        'data-tid': dataTid, feedback, innerRef, onClick, showLessText, showMoreText } = _a, 
        // pass all other props to Carbon
        otherCarbonProps = tslib_1.__rest(_a, ["ariaLabel", "className", "copyButtonDescription", "copyLabel", "copyToClipboard", 'data-tid', "feedback", "innerRef", "onClick", "showLessText", "showMoreText"]);
        const { wrapText } = this.props;
        const mappedProps = {
            ariaLabel: ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : ARIALABEL,
            copyButtonDescription: copyButtonDescription !== null && copyButtonDescription !== void 0 ? copyButtonDescription : COPYBUTTONDESCRIPTION,
            copyLabel: copyLabel !== null && copyLabel !== void 0 ? copyLabel : COPYLABEL,
            'data-tid': dataTid !== null && dataTid !== void 0 ? dataTid : DEFAULT_TID,
            feedback: feedback !== null && feedback !== void 0 ? feedback : FEEDBACK,
            showLessText: showLessText !== null && showLessText !== void 0 ? showLessText : SHOWLESSTEXT,
            showMoreText: showMoreText !== null && showMoreText !== void 0 ? showMoreText : SHOWMORETEXT,
        };
        if (className || wrapText) {
            mappedProps.className = `${className !== null && className !== void 0 ? className : ''} ${wrapText ? CLASSNAME_WRAP : ''}`;
        }
        if (this.invokeCopyToClipboard || onClick) {
            mappedProps.onClick = this.localOnClick;
        }
        if (innerRef) {
            mappedProps.ref = innerRef;
        }
        return (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(carbon_components_react_1.CodeSnippet, Object.assign({}, otherCarbonProps, mappedProps)),
            this.invokeCopyToClipboard && (react_1.default.createElement("textarea", { style: {
                    position: 'absolute',
                    top: 0,
                    opacity: 0,
                    zIndex: -1,
                }, ref: r => (this._input = r), readOnly: true, tabIndex: -1, value: this.props.children }))));
    }
}
const CodeSnippet = react_1.default.forwardRef((props, ref) => {
    const otherProps = ref ? { innerRef: ref } : {};
    return react_1.default.createElement(ForwardRefCodeSnippet, Object.assign({}, props, otherProps));
});
CodeSnippet.displayName = DISPLAY_NAME;
exports.default = CodeSnippet;
